<template>
  <div class="list-card h-100">
    <div class="card">
      <div class="card-header py-2">
        <h5 class="mb-0">{{ title }}</h5>
      </div>
      <div class="card-body pt-1 pb-2">
        <div v-if="loading" class="list-loading">
          <skeleton-loader type="list" />
        </div>
        <div v-else-if="isEmpty" class="empty-state">
          <feather-icon icon="AlertCircleIcon" size="32" class="text-muted mb-2" />
          <p>{{ emptyText }}</p>
        </div>
        <div v-else class="list-container">
          <ul class="list-group list-group-flush">
            <li
              v-for="(item, index) in items"
              :key="index"
              class="list-group-item d-flex justify-content-between align-items-center py-2"
            >
              <span class="item-name">{{ item.name }}</span>
              <span class="badge badge-pill" :class="badgeClass">{{ item.value }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonLoader from './SkeletonLoader.vue';

export default {
  name: 'ListCard',
  components: {
    SkeletonLoader
  },
  props: {
    title: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    emptyText: {
      type: String,
      default: 'No data available'
    },
    badgeVariant: {
      type: String,
      default: 'primary',
      validator: value =>
        ['primary', 'success', 'danger', 'warning', 'info', 'secondary'].includes(value)
    }
  },
  computed: {
    isEmpty() {
      return !this.items || this.items.length === 0;
    },
    badgeClass() {
      return `badge-${this.badgeVariant}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.list-card {
  .card {
    border-radius: 8px;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
    height: 100%;
    transition: all 0.3s ease-in-out;
  }

  .card-header {
    background-color: transparent;
    border-bottom: 1px solid rgba(34, 41, 47, 0.05);
  }

  .list-container {
    max-height: 300px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  }

  .list-loading,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    width: 100%;
  }

  .empty-state {
    color: #b9b9c3;
    text-align: center;
  }

  .list-group-item {
    transition: background-color 0.2s ease;
    border-left: none;
    border-right: none;

    &:first-child {
      border-top: none;
    }

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: rgba(115, 103, 240, 0.04);
    }

    .item-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 70%;
    }
  }

  .badge {
    min-width: 28px;
    padding: 0.35em 0.65em;
  }

  .badge-primary {
    background-color: #7367f0;
  }

  .badge-success {
    background-color: #28c76f;
  }

  .badge-danger {
    background-color: #ea5455;
  }

  .badge-warning {
    background-color: #ff9f43;
  }

  .badge-info {
    background-color: #00cfe8;
  }

  .badge-secondary {
    background-color: #82868b;
  }
}
</style>
